package com.rosario.juridico.platform

import io.ktor.client.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName
import kotlinx.serialization.json.Json

@Serializable
data class ChatMessage(
    val role: String,
    val content: String
)

@Serializable
data class ChatRequest(
    val prompt: String,
    val context: String? = null,
    val valorJus: Double? = null,
    val history: List<ChatMessage>? = null
)

@Serializable
data class ChatResponse(
    val result: String = "",
    val usesRemaining: Int? = null,
    val error: String? = null,
    val outOfUses: Boolean = false,
    val isPremium: Boolean = false
)

@Serializable
data class PreferenceResponse(
    val id: String? = null,
    val init_point: String? = null,
    val error: String? = null
)

@Serializable
data class PreferenceRequest(
    val planId: String
)

@Serializable
data class IndiceItem(val fecha: String, val valor: Double)

@Serializable
data class IndiceCac(
    val fecha: String,
    val general: Double,
    val materiales: Double,
    @SerialName("mano_obra") val manoObra: Double
)

object BackendClient {
    // URL base dependiente de la plataforma para resolver problemas de CORS en Web
    private val BASE_URL = API_BASE_URL

    // Caché en memoria
    private var cacheICL: Map<String, Double>? = null
    private var cacheIPC: Map<String, Double>? = null
    private var cacheCAC: List<IndiceCac>? = null

    val client = HttpClient {
        expectSuccess = false
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                prettyPrint = true
            })
        }
        install(HttpTimeout) {
            requestTimeoutMillis = 60000 // Aumentado a 60s para evitar SocketTimeout
            connectTimeoutMillis = 20000 // Aumentado a 20s
            socketTimeoutMillis = 60000
        }
    }

    private val jsonParser = Json { ignoreUnknownKeys = true }

    suspend fun fetchCACData(): List<IndiceCac> {
        cacheCAC?.let { return it }
        return try {
            val response = client.get("https://www.juridicorosario.com/cac.json")
            if (response.status == HttpStatusCode.OK) {
                val list = jsonParser.decodeFromString<List<IndiceCac>>(response.bodyAsText())
                cacheCAC = list
                list
            } else {
                emptyList()
            }
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun fetchICLData(): Map<String, Double> {
        cacheICL?.let { return it }
        return try {
            val response = client.get("https://www.juridicorosario.com/icl.json")
            if (response.status == HttpStatusCode.OK) {
                val list = jsonParser.decodeFromString<List<IndiceItem>>(response.bodyAsText())
                val map = list.associate { it.fecha to it.valor }
                cacheICL = map
                map
            } else {
                println("API ICL HTTP Error: ${response.status}")
                emptyMap()
            }
        } catch (e: Exception) {
            println("UI ICL Error: ${e.message}")
            emptyMap()
        }
    }

    suspend fun fetchIPCData(): Map<String, Double> {
        cacheIPC?.let {
            println("DEBUG IPC 0: Usando caché en memoria.")
            return it
        }
        return try {
            println("DEBUG IPC 1: Iniciando descarga de internet...")
            val response = client.get("https://www.juridicorosario.com/ipc.json")
            println("DEBUG IPC 2: Status HTTP -> ${response.status}")

            if (response.status == HttpStatusCode.OK || response.status.value in 200..299) {
                val texto = response.bodyAsText()
                println("DEBUG IPC 3: Texto recibido (primeros 100 caracteres) -> ${texto.take(100)}")

                // Aquí es donde suele crashear si el JSON no es perfecto
                val list = jsonParser.decodeFromString<List<IndiceItem>>(texto)
                println("DEBUG IPC 4: Parseo exitoso. Cantidad de meses -> ${list.size}")

                val map = list.associate { it.fecha.take(7) to it.valor }
                println("DEBUG IPC 5: Mapa creado. Llaves de prueba -> ${map.keys.take(3)}")

                cacheIPC = map
                map
            } else {
                println("DEBUG IPC ERROR: El servidor no devolvió 200 OK.")
                emptyMap()
            }
        } catch (e: Exception) {
            // ¡ESTO REVELARÁ AL FANTASMA!
            println("DEBUG IPC CRASH MORTAL: ${e::class.simpleName} - ${e.message}")
            emptyMap()
        }
    }

    suspend fun postChat(endpoint: String, request: ChatRequest): ChatResponse {
        val uid = FirebaseProxy.getCurrentUserId() ?: try { FirebaseProxy.signInAnonymously() } catch (e: Exception) { null }
        
        if (uid == null) {
            return ChatResponse(error = "No se pudo identificar tu sesión. Verificá tu conexión.")
        }

        val idToken = try { FirebaseProxy.getIdToken() } catch (e: Exception) { null }

        if (idToken == null) {
            return ChatResponse(error = "Error de autenticación. Intentá reiniciar la app.")
        }

        return try {
            val response = client.post("$BASE_URL$endpoint") {
                header("Authorization", "Bearer $idToken")
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status == HttpStatusCode.OK) {
                jsonParser.decodeFromString<ChatResponse>(response.bodyAsText())
            } else if (response.status == HttpStatusCode.Unauthorized) {
                handleUnauthorized(endpoint, request, idToken)
            } else {
                handleGenericError(response)
            }
        } catch (e: Exception) {
            val errorMsg = "${e::class.simpleName}: ${e.message}"
            println("BackendClient ERROR DETALLADO: $errorMsg")

            if (e.message?.contains("timeout", true) == true) {
                ChatResponse(error = "El servidor tardó demasiado en responder. Reintenta en unos segundos.")
            } else if (e.message?.contains("Serializer") == true || e.message?.contains("Json") == true) {
                ChatResponse(error = "Error al procesar respuesta del servidor.")
            } else {
                ChatResponse(error = "Error de conexión ($errorMsg).")
            }
        }
    }

    private suspend fun handleUnauthorized(endpoint: String, request: ChatRequest, oldToken: String): ChatResponse {
        val freshToken = try { FirebaseProxy.getIdToken() } catch (e: Exception) { null }
        
        if (freshToken != null && freshToken != oldToken) {
            return try {
                val response = client.post("$BASE_URL$endpoint") {
                    header("Authorization", "Bearer $freshToken")
                    contentType(ContentType.Application.Json)
                    setBody(request)
                }
                val responseText = response.bodyAsText()
                if (response.status == HttpStatusCode.OK) {
                    jsonParser.decodeFromString<ChatResponse>(responseText)
                } else {
                    handleGenericError(response)
                }
            } catch (e: Exception) {
                ChatResponse(error = "Error al reintentar la conexión.")
            }
        }

        return ChatResponse(error = "Sesión no válida o expirada. Intentá nuevamente.")
    }

    private suspend fun handleGenericError(response: HttpResponse): ChatResponse {
        val responseText = response.bodyAsText()
        val backendError = try {
            jsonParser.decodeFromString<ChatResponse>(responseText).error
        } catch (e: Exception) {
            null
        }
        
        return when (response.status) {
            HttpStatusCode.Forbidden -> ChatResponse(error = backendError ?: "Acceso denegado (403).")
            HttpStatusCode.TooManyRequests -> ChatResponse(error = backendError ?: "Límite de usos alcanzado.", outOfUses = true)
            else -> ChatResponse(error = backendError ?: "Error del servidor (${response.status.value}).")
        }
    }

    suspend fun consultaLegal(prompt: String, history: List<ChatMessage>? = null): ChatResponse =
        postChat("/api", ChatRequest(prompt = prompt, history = history))

    suspend fun calculoHonorarios(prompt: String, context: String, valorJus: Double): ChatResponse =
        postChat("/api", ChatRequest(prompt, context, valorJus))

    /**
     * Crea una preferencia de pago en Mercado Pago llamando al backend seguro.
     */
    suspend fun crearPreferencia(planId: String): PreferenceResponse? {
        println("[BackendClient] Solicitando preferencia para plan: $planId")
        val idToken = try { FirebaseProxy.getIdToken() } catch (e: Exception) { 
            println("[BackendClient] Error obteniendo ID Token: ${e.message}")
            null 
        } 
        
        if (idToken == null) {
            println("[BackendClient] ERROR: No hay ID Token")
            return PreferenceResponse(error = "No se pudo obtener el token de sesión.")
        }
        
        val url = "$BASE_URL/api/crear-preferencia"

        return try {
            val response = client.post(url) {
                header("Authorization", "Bearer $idToken")
                contentType(ContentType.Application.Json)
                setBody(PreferenceRequest(planId))
            }

            println("[BackendClient] Respuesta del servidor: ${response.status}")

            val responseText = response.bodyAsText()
            if (response.status == HttpStatusCode.OK) {
                val prefData = jsonParser.decodeFromString<PreferenceResponse>(responseText)
                println("[BackendClient] Preferencia generada: ${prefData.id}")
                prefData
            } else {
                println("[BackendClient] Error en respuesta (HTTP ${response.status}): $responseText")
                val backendError = try {
                    jsonParser.decodeFromString<PreferenceResponse>(responseText).error
                } catch (e: Exception) {
                    null
                }
                PreferenceResponse(error = backendError ?: "Error del servidor (${response.status.value})")
            }
        } catch (e: Exception) {
            println("[BackendClient] EXCEPCIÓN llamando crearPreferencia: ${e.message}")
            PreferenceResponse(error = "Error de conexión: ${e.message}")
        }
    }

    /**
     * Invoca la Cloud Function para actualizar derechos premium (Server-Assisted).
     * Ahora utiliza el SDK de Firebase compatible con Gen 2.
     */
    suspend fun updatePremiumEntitlement(planId: String, source: String, purchaseId: String? = null): Boolean {
        val result = FirebaseProxy.callFunction(
            name = "updatePremiumEntitlement",
            data = mapOf(
                "planId" to planId,
                "source" to source,
                "purchaseId" to (purchaseId ?: "none")
            )
        )
        // Si el resultado no es nulo, asumimos éxito ya que el SDK lanza excepción en caso de error HTTP
        return result != null
    }
}
